import {
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  NovaPoshtaCitySelect,
  NovaPoshtaWarehouseSelect,
  NovaPoshtaContactPersonSelect,
} from '../../../common/novaposhta';
import type { ShipmentFormValues } from '../useCreateShipment';
import type { ShippingSender } from '../../../../types/shippingSender';

interface ShipmentFormProps {
  values: ShipmentFormValues;
  onChange: (values: ShipmentFormValues) => void;
  senders: ShippingSender[];
  apiKey: string | null;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
}

export default function ShipmentForm({
  values,
  onChange,
  senders,
  apiKey,
  submitting,
  error,
  onSubmit,
}: ShipmentFormProps) {
  const setField = <K extends keyof ShipmentFormValues>(field: K, value: ShipmentFormValues[K]) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Отримувач</Typography>
      <TextField
        label="Прізвище отримувача"
        value={values.recipientLastName}
        onChange={(e) => setField('recipientLastName', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Ім'я отримувача"
        value={values.recipientFirstName}
        onChange={(e) => setField('recipientFirstName', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="По батькові отримувача"
        value={values.recipientMiddleName}
        onChange={(e) => setField('recipientMiddleName', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        helperText="Необов’язково"
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Телефон отримувача"
        value={values.recipientPhone}
        onChange={(e) => setField('recipientPhone', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <Box sx={{ mb: 1.5 }}>
        <NovaPoshtaCitySelect
          apiKey={apiKey}
          value={values.recipientCity}
          onChange={(city) => onChange({ ...values, recipientCity: city, recipientWarehouse: null })}
          label="Місто отримувача"
          disabled={submitting}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <NovaPoshtaWarehouseSelect
          apiKey={apiKey}
          cityRef={values.recipientCity?.ref ?? null}
          value={values.recipientWarehouse}
          onChange={(warehouse) => setField('recipientWarehouse', warehouse)}
          label="Відділення/поштомат отримувача"
          disabled={submitting}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Відправлення</Typography>
      <TextField
        select
        label="Відправник"
        value={values.senderId}
        onChange={(e) => setField('senderId', Number(e.target.value))}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      >
        {senders.map((sender) => (
          <MenuItem key={sender.id} value={sender.id}>{sender.label}</MenuItem>
        ))}
      </TextField>
      <Box sx={{ mb: 1.5 }}>
        <NovaPoshtaContactPersonSelect
          apiKey={apiKey}
          value={values.senderContactPerson}
          onChange={(person) => setField('senderContactPerson', person)}
          label="Контактна особа відправника"
          disabled={submitting}
        />
      </Box>
      <TextField
        label="Вага, кг"
        type="number"
        value={values.weight}
        onChange={(e) => setField('weight', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Кількість коробок"
        type="number"
        value={values.seatsAmount}
        onChange={(e) => setField('seatsAmount', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Оголошена вартість"
        type="number"
        value={values.declaredValue}
        onChange={(e) => setField('declaredValue', e.target.value)}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
      />
      {/* Спосіб оплати не обирається — лише готівка (CASH): безготівковий розрахунок
          вимагає окремого договору з Новою поштою, див. types/shipment.ts. */}
      <TextField
        select
        label="Платник за доставку"
        value={values.payerType}
        onChange={(e) => setField('payerType', e.target.value as ShipmentFormValues['payerType'])}
        disabled={submitting}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      >
        <MenuItem value="SENDER">Відправник</MenuItem>
        <MenuItem value="RECIPIENT">Отримувач</MenuItem>
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            checked={values.codEnabled}
            onChange={(e) => setField('codEnabled', e.target.checked)}
            disabled={submitting}
          />
        }
        label="Наложений платіж (отримувач платить за товар при отриманні)"
        sx={{ mb: values.codEnabled ? 0.5 : 2 }}
      />
      {values.codEnabled && (
        <TextField
          label="Сума наложеного платежу"
          type="number"
          value={values.codAmount}
          onChange={(e) => setField('codAmount', e.target.value)}
          disabled={submitting}
          fullWidth
          size="small"
          helperText="Комісію за грошовий переказ Нової пошти сплачує отримувач"
          sx={{ mb: 2 }}
        />
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={onSubmit}
          disabled={submitting}
        >
          Створити ТТН
        </Button>
      </Box>
    </Box>
  );
}
